import { useState, useRef, useEffect, useCallback } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CircularProgress } from '@mui/material';
import ModalWrapper from '../../ModalWrapper';
import { AutoSuggest, CommonFooter, InputBox } from '../../FormComponent';
import {
  generateUploadUrl,
  uploadFileToS3,
  registerExecutionFile,
  saveExecutionFile,
} from '../../../utils/api/executionFileApis';
import { getExecutionFileFolders } from '../../../utils/api/executionFileFoldersApis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { useSnackbar } from '../../../utils/useSnackbar';
import { useScanningStatusSSE } from '../../../hooks/useScanningStatusSSE';
import {
  MAX_FILE_SIZE,
  ALLOWED_MIME_TYPES,
  ALLOWED_EXTENSIONS,
  ACCEPTED_FILE_TYPE_LABELS,
} from '../../AssetsManagement/constants';
import { flattenFolders, formatFileSize } from '../../AssetsManagement/helpers';
import FileTypeChip from '../../AssetsManagement/FileTypeChip';
import { interpretQuickUploadSaveResult } from './quickUploadFinishSave';
import styles from '../../../styles/formComponent/FormComponent.module.scss';

/**
 * Quick file upload modal for Record page. User selects destination folder.
 * Flow: generate URL → upload to S3 → register (SCANNING) → virus scan (SSE)
 * → save with executionFileId (updates to CLEAN) → onSuccess.
 */
const QuickFileUploadModal = ({ onSuccess, onClose }) => {
  const [folderList, setFolderList] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [folderError, setFolderError] = useState(null);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileNameError, setFileNameError] = useState('');
  const [description, setDescription] = useState('');
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState('');

  const [uploadStep, setUploadStep] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [scanFileKey, setScanFileKey] = useState(null);
  const [scanError, setScanError] = useState(null);

  const inputRef = useRef(null);
  const saveCalledRef = useRef(false);
  const executionFileIdRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  const isUploading = uploadStep === 'uploading';
  const isScanning = uploadStep === 'scanning';
  const isSaving = uploadStep === 'saving';
  const isBusy = isUploading || isScanning || isSaving;

  const { scannedStatus, isScanningComplete } = useScanningStatusSSE(scanFileKey, isScanning);

  useEffect(() => {
    (async () => {
      try {
        const res = await getExecutionFileFolders();
        if (res.status === STATUS.SUCCESS && res.data?.length > 0) {
          const flat = flattenFolders(res.data);
          setFolderList(flat);
          setSelectedFolderId('');
          setFolderError(null);
        } else {
          setFolderList([]);
          setSelectedFolderId('');
          setFolderError('No folder available. Create a folder in (File Manager) first.');
        }
      } catch {
        setFolderList([]);
        setSelectedFolderId('');
        setFolderError('Failed to load folders.');
      } finally {
        setFoldersLoading(false);
      }
    })();
  }, []);

  const handleScanResult = useCallback(async () => {
    const registeredId = executionFileIdRef.current;
    if (!scannedStatus || !scanFileKey || !registeredId) return;
    if (saveCalledRef.current) return;
    saveCalledRef.current = true;

    if (scannedStatus === 'CLEAN' && selectedFolderId) {
      setUploadStep('saving');
      try {
        const fileMeta = file ? { size: file.size, mimeType: file.type, originalName: file.name } : undefined;
        const saveRes = await saveExecutionFile({
          executionFileId: registeredId,
          tempFileKey: scanFileKey,
          folderId: selectedFolderId,
          fileName: fileName.trim(),
          fileDescription: description.trim() || undefined,
          fileMetadata: fileMeta,
        });

        const finish = interpretQuickUploadSaveResult(saveRes);
        if (!finish.ok) {
          setScanError(finish.error);
          setUploadStep('scan_error');
          return;
        }

        setUploadStep('done');
        showSnackbar(finish.snackbarMessage, finish.snackbarSeverity);
        onSuccess?.(finish.payload);
        onClose?.();
      } catch {
        setScanError('Unexpected error saving file');
        setUploadStep('scan_error');
      }
    } else {
      setScanError(
        scannedStatus === 'INFECTED'
          ? 'This file contains a virus and has been removed.'
          : 'Scan encountered an error. Please try uploading again.'
      );
      setUploadStep('scan_error');
    }
  }, [scannedStatus, scanFileKey, selectedFolderId, fileName, description, file, onSuccess, onClose, showSnackbar]);

  useEffect(() => {
    if (isScanningComplete && isScanning) {
      handleScanResult();
    }
  }, [isScanningComplete, isScanning, handleScanResult]);

  const validateFile = (f) => {
    if (!ALLOWED_MIME_TYPES.includes(f.type))
      return `File type not allowed. Accepted: ${ALLOWED_EXTENSIONS}`;
    if (f.size > MAX_FILE_SIZE)
      return `File is too large (${formatFileSize(f.size)}). Maximum is 2 MB.`;
    return null;
  };

  const selectFile = (f) => {
    const err = validateFile(f);
    setFileError(err || '');
    if (!err) {
      setFile(f);
      setFileName((prev) => (prev.trim() ? prev : f.name));
    } else {
      setFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) selectFile(dropped);
  };

  const handleUpload = async () => {
    if (!file || !selectedFolderId) return;

    const trimmedName = fileName.trim();
    if (!trimmedName) {
      setFileNameError('File name is required');
      return;
    }
    if (trimmedName.length > 200) {
      setFileNameError('File name must be 200 characters or fewer');
      return;
    }
    setFileNameError('');

    try {
      saveCalledRef.current = false;
      executionFileIdRef.current = null;
      setUploadStep('uploading');
      setProgress(10);

      const urlRes = await generateUploadUrl({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });
      if (urlRes.status !== STATUS.SUCCESS) {
        showSnackbar(urlRes.error || 'Failed to generate upload URL', 'error');
        setUploadStep('idle');
        return;
      }
      const { uploadUrl, fields, fileKey } = urlRes.data;
      setProgress(40);

      const s3Res = await uploadFileToS3({ uploadUrl, fields, file });
      if (!s3Res.success) {
        showSnackbar(s3Res.error || 'S3 upload failed', 'error');
        setUploadStep('idle');
        return;
      }
      setProgress(100);

      // Same as File Manager: register SCANNING row, then save updates it to CLEAN.
      const fileMeta = { size: file.size, mimeType: file.type, originalName: file.name };
      const registerRes = await registerExecutionFile({
        tempFileKey: fileKey,
        folderId: selectedFolderId,
        fileName: fileName.trim(),
        fileDescription: description.trim() || undefined,
        fileMetadata: fileMeta,
      });
      if (registerRes.status !== STATUS.SUCCESS || !registerRes.data?.id) {
        showSnackbar(registerRes.error || 'Failed to register file for scanning', 'error');
        setUploadStep('idle');
        return;
      }

      executionFileIdRef.current = registerRes.data.id;
      setScanFileKey(fileKey);
      setUploadStep('scanning');
    } catch {
      showSnackbar('Unexpected error during upload', 'error');
      setUploadStep('idle');
    }
  };

  const dropZoneClass = [
    styles.dropZone,
    dragging ? styles.dragging : '',
    file ? styles.hasFile : '',
    fileError ? styles.hasError : '',
  ].filter(Boolean).join(' ');

  const getFooterButtons = () => {
    if (uploadStep === 'scan_error') {
      return [
        { label: 'Close', variant: 'primary', className: 'white bg-blue600', onClick: onClose },
      ];
    }
    return [
      { label: 'Cancel', variant: 'outline', className: 'primary', onClick: onClose, disabled: isBusy },
      {
        label: isUploading ? 'Uploading…' : 'Upload',
        variant: 'primary',
        className: 'white bg-blue600',
        onClick: handleUpload,
        disabled:
          !file ||
          isBusy ||
          !!fileError ||
          !fileName.trim() ||
          !selectedFolderId ||
          foldersLoading,
        loading: isUploading,
      },
    ];
  };

  return (
    <ModalWrapper
      isOpen
      onClose={onClose}
      closeDisabled={isBusy}
      closeOnEsc={!isBusy}
      title="Quick file upload"
      size="medium"
      footer={<CommonFooter buttons={getFooterButtons()} />}
    >
      <div className={styles.uploadBody}>
        {(isScanning || isSaving) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              padding: '32px 0',
            }}
          >
            <ShieldCheckIcon style={{ width: 48, height: 48, color: '#2563eb' }} />
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
              {isSaving ? 'Saving file…' : 'Scanning for viruses…'}
            </p>
            <CircularProgress size={28} sx={{ color: '#2563eb' }} />
            <p style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center', maxWidth: 320 }}>
              {isSaving
                ? 'Scan passed! Moving file to its permanent location…'
                : 'Please wait while ClamAV checks your file. This usually takes a few seconds.'}
            </p>
          </div>
        )}

        {uploadStep === 'scan_error' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              padding: '32px 0',
            }}
          >
            <ExclamationTriangleIcon style={{ width: 48, height: 48, color: '#dc2626' }} />
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#dc2626' }}>Upload Failed</p>
            <p style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
              {scanError}
            </p>
          </div>
        )}

        {!isScanning && !isSaving && uploadStep !== 'scan_error' && (
          <>
            {foldersLoading && (
              <div style={{ padding: '12px 0', fontSize: 13, color: '#64748b' }}>
                Loading folders…
              </div>
            )}
            {!foldersLoading && folderError && (
              <p className={styles.uploadFileError} style={{ marginBottom: 12 }}>
                <ExclamationCircleIcon />
                {folderError}
              </p>
            )}
            {!foldersLoading && folderList.length > 0 && (
              <>
                <div
                  className={dropZoneClass}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onClick={() => !file && inputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && !file && inputRef.current?.click()
                  }
                  aria-label="Drop file here or press Enter to browse"
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept={ALLOWED_EXTENSIONS}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) selectFile(f);
                      // Allow picking the same file again: browsers often won't fire `onChange`
                      // if the input's value doesn't change.
                      e.target.value = '';
                    }}
                  />
                  {file ? (
                    <div className={styles.selectedFileCard}>
                      <div className={styles.fileIconBox}>
                        <DocumentIcon />
                      </div>
                      <div className={styles.selectedFileInfo}>
                        <p className={styles.sfName} title={file.name}>
                          {file.name}
                        </p>
                        <p className={styles.sfSize}>{formatFileSize(file.size)}</p>
                      </div>
                      <FileTypeChip mimeType={file.type} fileName={file.name} />
                      {!isUploading && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                            setFileError('');
                            setFileName('');
                            setFileNameError('');
                            setDescription('');
                            setUploadStep('idle');
                            setProgress(0);
                            setScanFileKey(null);
                            setScanError(null);
                            if (inputRef.current) inputRef.current.value = '';
                          }}
                          title="Remove file"
                          aria-label="Remove selected file"
                        >
                          <XMarkIcon />
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className={styles.dropIcon} />
                      <p className={styles.dropTitle}>
                        Drop a file here, or <span>browse</span>
                      </p>
                      <p className={styles.dropHint}>Max 2 MB · {ALLOWED_EXTENSIONS}</p>
                    </>
                  )}
                </div>

                {fileError && (
                  <p className={styles.uploadFileError}>
                    <ExclamationCircleIcon />
                    {fileError}
                  </p>
                )}

                {isUploading && (
                  <div className={styles.progressWrap}>
                    <p className={styles.progressLabel}>Uploading to storage…</p>
                    <div className={styles.progressTrack}>
                      <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                )}

               <AutoSuggest
                  label="Destination folder"
                  name="folder"
                  value={selectedFolderId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedFolderId(v?.value ?? v ?? '');
                  }}
                  options={folderList.map((f) => ({
                    value: f.id,
                    label: f.folderPath || f.folderName,
                  }))}
                  disabled={isUploading}
                  size="medium"
                  placeholder="Search or select folder..."
                  openOnFocus
                  noOptionsText="No folders match"
                  required
                />
                
                <InputBox
                  label="File name"
                  value={fileName}
                  onChange={(e) => {
                    setFileName(e.target.value);
                    if (fileNameError) setFileNameError('');
                  }}
                  placeholder="e.g. execution-report.pdf"
                  disabled={isUploading}
                  maxLength={200}
                  error={!!fileNameError}
                  helperText={fileNameError}
                  required
                />

                <InputBox
                  label="Description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this file…"
                  multiline
                  rows={2}
                  disabled={isUploading}
                  maxLength={500}
                />

                <div className={styles.allowedRow}>
                  <p className={styles.allowedLabel}>Accepted file types</p>
                  <div className={styles.allowedChips}>
                    {ACCEPTED_FILE_TYPE_LABELS.map((label) => (
                      <FileTypeChip key={label} fileName={`.${label.toLowerCase()}`} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ModalWrapper>
  );
};

export default QuickFileUploadModal;
